export type MeetingState = 'waiting' | 'open' | 'ended' | 'missing';

export interface MeetingView {
  code: string;
  title: string;
  startsAt: number | null;
  endsAt: number | null;
  scheduled: boolean;
  state: MeetingState;
  opensAt?: number;
  opensInMs?: number;
  participants?: number;
}

export interface JoinCredentials {
  token: string;
  wsUrl: string;
  identity: string;
  room: string;
  title: string;
  /**
   * Организатор ли это.
   *
   * Значение приходит с сервера и продублировано в подписанных метаданных
   * токена — здесь оно только для того, чтобы интерфейс не ждал подключения
   * к комнате. Решения о правах принимаются по метаданным, не по этому полю.
   */
  host: boolean;
}

export interface HealthInfo {
  status: string;
  livekit: 'up' | 'down';
  mail: 'smtp' | 'console';
}

export interface CurrentUser {
  email: string;
}

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status = 0,
    public data: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      // Сессия живёт в httpOnly-cookie, её нужно слать со всеми запросами.
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      ...init,
    });
  } catch {
    throw new ApiError('NETWORK', 'Сервер GTO CONNECT недоступен. Проверьте, что он запущен.');
  }

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new ApiError(
      body.error ?? 'UNKNOWN',
      body.message ?? 'Не удалось выполнить запрос',
      response.status,
      body,
    );
  }

  return body as T;
}

const post = <T>(path: string, payload?: unknown) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(payload ?? {}) });

export const api = {
  health: () => request<HealthInfo>('/health'),

  // ── Вход ──────────────────────────────────────────────────────────
  me: () => request<{ user: CurrentUser | null; domain: string }>('/auth/me'),
  requestCode: (email: string) => post<{ sent: boolean; delivery: string }>('/auth/request', { email }),
  verifyCode: (email: string, code: string) => post<{ user: CurrentUser }>('/auth/verify', { email, code }),
  logout: () => post<{ ok: boolean }>('/auth/logout'),

  // ── Встречи ───────────────────────────────────────────────────────
  createMeeting: (payload: { title?: string; startsAt?: number; durationMinutes?: number }) =>
    post<{ meeting: MeetingView }>('/meetings', payload),
  myMeetings: () => request<{ meetings: MeetingView[] }>('/meetings'),
  getMeeting: (code: string) =>
    request<{ meeting: MeetingView }>(`/meetings/${encodeURIComponent(code)}`),
  cancelMeeting: (code: string) =>
    request<{ ok: boolean }>(`/meetings/${encodeURIComponent(code)}`, { method: 'DELETE' }),

  getToken: (room: string, name: string) => post<JoinCredentials>('/token', { room, name }),

  /**
   * Включает или выключает режим презентации.
   *
   * Право на это проверяет сервер по владельцу встречи — здесь только вызов.
   */
  setPresentation: (room: string, on: boolean) =>
    post<{ presentation: boolean }>(`/meetings/${encodeURIComponent(room)}/presentation`, { on }),
};
